import { FC, useEffect, useRef } from 'react';
import { ButtonLink, Label, PopupStateProps, TextInput, TextInputSizes, Toggle } from '@silverfort/sf-components';
import { RecipientType, SendEnrollmentEmailPopupState } from '../types';
import { Entity, UserSession } from '../../../../../api';
import { Controller, FormProvider } from 'react-hook-form';
import { EnrollmentEmailFormValues } from '../../../types/forms';
import { ToolbarItemKey } from '@silverfort/sf-components/dist/RichTextEditor/types';
import { RecipientSection } from './components/RecipientSection';
import { TokenSection } from './components/TokenSection';
import { EmailContentSection } from './components/EmailContentSection';
import { useSendEnrollmentRequestForm } from '../hooks/useSendEnrollmentRequestForm';
import { useGetEnrollmentEmailsByTokenTypeId } from '../../../api/queries/useGetEnrollmentEmailsByTokenTypeId';

const RICH_TEXT_EDITOR_VISIBLE_ITEMS: ToolbarItemKey[] = [
    'undo',
    'redo',
    'heading',
    'bold',
    'italic',
    'underline',
    'bulletList',
    'numberedList',
    'image',
    'link',
    'reset'
];

const OFFLINE_RICH_TEXT_EDITOR_VISIBLE_ITEMS = RICH_TEXT_EDITOR_VISIBLE_ITEMS.filter((item) => item !== 'image');

type CustomizeEnrollmentMailContentProps = PopupStateProps<SendEnrollmentEmailPopupState> & {
    onRecipientTypeChange: (
        selectedType: RecipientType,
        selectedEntities: Entity[],
        formValues: EnrollmentEmailFormValues
    ) => void;
    onPreviewButtonClick: (selectedEntities: Entity[], formValues: EnrollmentEmailFormValues, isValid: boolean) => void;
    isOfflineMode: boolean;
    isRecipientSectionVisible?: boolean;
    userSession?: UserSession;
};
const CustomizeEnrollmentEmailContent: FC<CustomizeEnrollmentMailContentProps> = ({
    onRecipientTypeChange,
    onPreviewButtonClick,
    isOfflineMode,
    isRecipientSectionVisible = true,
    userSession,
    popupState,
    setPopupState
}) => {
    const editorRef = useRef<HTMLDivElement | null>(null);

    const visibleToolbarItems = isOfflineMode ? OFFLINE_RICH_TEXT_EDITOR_VISIBLE_ITEMS : RICH_TEXT_EDITOR_VISIBLE_ITEMS;

    const { selectedEntities: entities, formValues } = popupState;
    const { formMethods } = useSendEnrollmentRequestForm(formValues);

    const { control, watch, setValue } = formMethods;

    const selectedTokenTypeId = watch('tokenTypeId');
    const isGenerateEmailToggled = watch('isCustomEmail');

    const { enrollmentEmails } = useGetEnrollmentEmailsByTokenTypeId(selectedTokenTypeId);

    const handleUpdateFormInnerAndOuterState = <K extends keyof EnrollmentEmailFormValues>(
        key: K,
        value: EnrollmentEmailFormValues[K]
    ) => {
        const currentVal = formValues[key];
        if (currentVal === value) return;

        setPopupState((prevState) => ({
            ...prevState,
            formValues: {
                ...prevState.formValues,
                [key]: value
            }
        }));

        setValue(key, value as any, { shouldDirty: true, shouldValidate: true });
    };

    const handleRecipientTypeChange = (selectedType: RecipientType) => {
        handleUpdateFormInnerAndOuterState('recipientType', selectedType);
        onRecipientTypeChange(selectedType, entities, formValues);

        if (selectedType === 'unpaired') {
            setPopupState({ ...popupState, selectedEntities: [] });
        }
    };

    const handleUnselectEntity = (entityGuid: string) => {
        setPopupState({ ...popupState, selectedEntities: entities.filter((entity) => entity.guid !== entityGuid) });
    };

    useEffect(() => {
        if (!!entities && entities.length > 0) {
            handleUpdateFormInnerAndOuterState('recipientType', 'groupOU');
        } else {
            handleUpdateFormInnerAndOuterState('recipientType', 'unpaired');
        }
    }, [entities.length, handleUpdateFormInnerAndOuterState]);

    useEffect(() => {
        if (enrollmentEmails && formMethods.formState.dirtyFields.tokenTypeId) {
            const email = enrollmentEmails.customTemplate || enrollmentEmails.defaultTemplate;

            handleUpdateFormInnerAndOuterState('subject', email.subject);
            handleUpdateFormInnerAndOuterState('content', email.content);
            handleUpdateFormInnerAndOuterState('isCustomEmail', email.isCustomEmail);
        }
    }, [enrollmentEmails]);

    useEffect(() => {
        if (isGenerateEmailToggled) {
            editorRef.current?.scrollIntoView({ behavior: 'smooth' });
        }
    }, [isGenerateEmailToggled]);

    useEffect(() => {
        setPopupState((prevState) => ({ ...prevState, isValid: formMethods.formState.isValid }));
    }, [formMethods.formState.isValid]);

    useEffect(() => {
        formMethods.trigger().then((isValid) => setPopupState((prevState) => ({ ...prevState, isValid })));
    }, [formMethods.trigger]);

    return (
        <FormProvider {...formMethods}>
            <div className="font-sf w-full h-fit flex flex-col py-2 gap-2">
                <span className="text-13 font-medium h-4">Generate request</span>
                <div
                    className={`flex-shrink-0 rounded-lg border border-sf-gray-100 py-4 ${
                        isRecipientSectionVisible ? 'h-[226px]' : 'h-[164px]'
                    }`}>
                    <div className="h-full">
                        {isRecipientSectionVisible && (
                            <>
                                <RecipientSection
                                    selectedEntities={entities}
                                    userSession={userSession}
                                    onUnselectEntity={handleUnselectEntity}
                                    onRecipientTypeChange={handleRecipientTypeChange}
                                />
                                <div className="border-t border-sf-gray-100 my-4" />
                            </>
                        )}
                        <TokenSection
                            onTokenTypeChange={(selectTokenType) => {
                                //@ts-ignore TODO: this issue might be solved in the components library since it doesnt know how to get generics (and we want type of closed values here)
                                handleUpdateFormInnerAndOuterState('tokenType', selectTokenType);
                                handleUpdateFormInnerAndOuterState('tokenTypeId', undefined);
                                handleUpdateFormInnerAndOuterState('isCustomEmail', false);
                            }}
                            onTokenTypeIdChange={(selectedTokenTypeId) => {
                                //@ts-ignore TODO: this issue might be solved in the components library since it doesnt know how to get generics (and we want type of closed values here)
                                handleUpdateFormInnerAndOuterState('tokenTypeId', selectedTokenTypeId);
                                handleUpdateFormInnerAndOuterState('isCustomEmail', false);
                            }}
                        />
                        <div className="border-t border-sf-gray-100 my-4" />
                        <div className="px-4">
                            <Controller
                                control={control}
                                name="isCustomEmail"
                                render={({ field: { value } }) => (
                                    <Toggle
                                        disabled={selectedTokenTypeId === undefined}
                                        className="mb-0"
                                        checked={value}
                                        label="Generate email"
                                        onChange={(checked) => {
                                            handleUpdateFormInnerAndOuterState('isCustomEmail', checked);
                                        }}
                                    />
                                )}
                            />
                        </div>
                    </div>
                </div>
                {isGenerateEmailToggled && (
                    <div className="flex flex-col gap-2" ref={editorRef}>
                        <EmailContentSection
                            defaultEmailTitle={enrollmentEmails?.defaultTemplate.subject}
                            defaultEmailContent={enrollmentEmails?.defaultTemplate.content}
                            visibleToolbarItems={visibleToolbarItems}
                            onChange={handleUpdateFormInnerAndOuterState}
                        />
                        <div className="flex flex-row gap-10 p-4 pr-6 h-16 border rounded-[8px] items-center border-sf-gray-100">
                            <Label text="Company logo" className="text-13 font-medium flex-shrink-0" />
                            <Controller
                                control={control}
                                name="companyLogoUrl"
                                render={({ field: { value } }) => (
                                    <TextInput
                                        value={value}
                                        inputSize={TextInputSizes.Small}
                                        className="w-full"
                                        wrapperClassName="w-full"
                                        placeholder="Enter logo URL"
                                        onChange={(text) => {
                                            handleUpdateFormInnerAndOuterState('companyLogoUrl', text);
                                        }}
                                    />
                                )}
                            />
                        </div>

                        <ButtonLink
                            className="text-[13px] self-end"
                            onClick={() => {
                                onPreviewButtonClick(entities, formValues, formMethods.formState.isValid);
                            }}
                            text="Preview"
                        />
                    </div>
                )}
            </div>
        </FormProvider>
    );
};

export default CustomizeEnrollmentEmailContent;
